import {Injectable} from '@angular/core';
import {CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router} from '@angular/router';
import {DataStorageService} from './data-storage.service';
import {Authorization} from '../models/authorization';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {ConfigService} from './config.service';
import {Associate} from '../models/associate';

@Injectable()
export class SecurityService implements CanActivate {

  authorization: Authorization;

  constructor(private router: Router,
              private dataStorageService: DataStorageService,
              private http: HttpClient,
              private config: ConfigService) {
    this.authorization = new Authorization;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean {
    if (this.isLoggedIn()) {
      switch (route.url[route.url.length - 1].path) { // last part of url
        case 'management':
          return this.isAdmin();
        case 'mydevmgnt':
          return this.isLeader();
        case 'editManagement':
          return this.isAdmin();
        default:
          return this.isUser();

      }
    }
    return false;
  }

  isLoggedIn(): boolean {
    if (this.authorization.access_token !== '' && this.dataStorageService.disclaimeerOn || this.authorization.access_token !== '') {
      return true;
    }
    return false;
  }

  getTokenWithCode(code: string, partnerId: string): Promise<any> {
    const url = this.config.host + '/oauth/token?grant_type=password';
    const body = new HttpParams().set('username', encodeURIComponent(code)).set('password', encodeURIComponent(partnerId));
    const opt = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')};
    return new Promise<any>((resolve, reject) => {
      this.http.post(url, body.toString(), opt).toPromise().then(response => {
       // console.log('response', response);
        this.authorization.access_token = response['access_token'];
        this.authorization.token_type = response['token_type'];
        this.authorization.refresh_token = response['refresh_token'];
        this.authorization.expires_in = response['expires_in'];
        this.authorization.scope = response['scope'];
        this.authorization.user.username = response['user'].username;
        this.authorization.user.name = response['user'].name;
        this.authorization.user.partnerName = response['user'].partnerName;
        this.authorization.user.partnerId = response['user'].partnerId;
        this.authorization.user.authorities = response['user'].authorities;
        resolve();
      }).catch(error => {
        reject(error);
        this.dataStorageService.invalidCredentials = 'Invalid Credentials';
      });
    });
  }


  login(user: string, password: string): Promise<any> {
    const url = this.config.host + '/oauth/token?grant_type=password';
    // in angular 5.x we cannot use the HttpUrlEncodingCodec option which is bugged.
    // https://github.com/angular/angular/pull/9651#discussion-diff-74046594
    const body = new HttpParams().set('username', encodeURIComponent(user)).set('password', encodeURIComponent(password));
    const opt = {headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')};
    return new Promise<any>((resolve, reject) => {
      this.http.post(url, body.toString(), opt).toPromise().then(response => {
        this.authorization.access_token = response['access_token'];
        this.authorization.token_type = response['token_type'];
        this.authorization.refresh_token = response['refresh_token'];
        this.authorization.expires_in = response['expires_in'];
        this.authorization.scope = response['scope'];
        this.authorization.user.username = response['user'].username;
        this.authorization.user.name = response['user'].name;
        this.authorization.user.partnerName = response['user'].partnerName;
        this.authorization.user.partnerId = response['user'].partnerId;
        this.authorization.user.authorities = response['user'].authorities;
        resolve();
      }).catch(error => {
        reject(error);
        this.dataStorageService.invalidCredentials = 'Invalid Credentials';
        // this.logout();
      });
    });
  }

  logout(): void {
    this.dataStorageService.newOfferOpened = false;
    this.dataStorageService.clean();
    this.authorization = new Authorization();
    //  this.router.navigate(['/login']);
    this.dataStorageService.disclaimeerOn = false;
    localStorage.clear();
    window.location.href = this.config.oauthUrl + '/en_CH/logout?client_id=' + this.config.clien_id + '&redirect_uri=' + this.config.authbackUrl + '&application=' ;
  }

  isAdmin(): boolean {
    return this.authorization.user.authorities.indexOf('ROLE_ADMIN') !== -1 ? true : false;
  }

  isUser(): boolean {
    return this.authorization.user.authorities.indexOf('ROLE_USER') !== -1 ? true : false;
  }

  isLeader(): boolean {
    return this.authorization.user.authorities.indexOf('ROLE_LEADER') !== -1 ? true : false;
  }

  isEditable(): boolean {
    return this.dataStorageService.currentState.offerID === 0 ? false : true;
  }

  waitForRefreshToFinish(): Promise<any> { // resolves when refresh is not in progress
    return new Promise<any>((resolve) => {
      if (this.dataStorageService.refreshInProgress) {
        const handler = setInterval(() => {
          if (!this.dataStorageService.refreshInProgress) {
            clearInterval(handler);
            resolve();
          }
        }, 100);
      } else {
        resolve();
      }
    });

  }

  refreshToken(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.dataStorageService.refreshInProgress) {
        this.waitForRefreshToFinish().then(() => {
          resolve();
        });
      } else {
        this.dataStorageService.refreshInProgress = true;
        const url = this.config.host + '/oauth/token?'
          + 'grant_type=' + 'refresh_token'
          + '&refresh_token=' + this.authorization.refresh_token;
        this.http.request<any>('POST', url).toPromise().then(response => {
          this.authorization.access_token = response.access_token;
          this.authorization.token_type = response.token_type;
          this.authorization.refresh_token = response.refresh_token;
          this.authorization.expires_in = response.expires_in;
          this.authorization.scope = response.scope;
          this.authorization.user.username = response.user.username;
          this.authorization.user.name = response.user.name;
          this.authorization.user.partnerName = response.user.partnerName;
          this.authorization.user.authorities = response.user.authorities;
          this.dataStorageService.refreshInProgress = false;
          resolve();
        }).catch(error => {
          // console.error(error);
          this.dataStorageService.refreshInProgress = false;
          this.logout();
          reject();
        });
      }
    });
  }

  getPartnersAssociates(): Promise<Associate[]> {
    const url = this.config.host
      + '/api/account'
      + '?partner-id='
      + this.authorization.user.partnerId;

    return new Promise((resolve, reject) => {
      this.http.get<Associate[]>(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
              this.refreshToken().then(() => {
                this.getPartnersAssociates().then(response => resolve(response));
              });
            } else {
              // console.error(error);
              reject(error);
            }
          }
        });
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/security.service.ts